import { LANGUAGES } from '@/constants';
import { useGlobalState } from '@/hooks';
import { type ILanguage, LanguageCode } from '@/interfaces';
import { useRouter } from 'next/router';
import { type FC, useEffect, useRef, useState } from 'react';

import {
  ArrowIcon,
  CurrentLanguageItem,
  CurrentLanguageWrapper,
  LanguageIcon,
  LanguageItem,
  LanguageList,
  LanguageWrapper,
} from './Languages.styles';

const getPmtLangHref = (languageCode: string, asPath: string) => {
  const splitPath = asPath.split('/');
  const code = splitPath[1];

  const hasLanguageCodeOnUrl = Object.values<string>(LanguageCode).includes(code);
  const isEnglish = languageCode === LanguageCode.English;

  if (hasLanguageCodeOnUrl && isEnglish) return asPath.replace(`/${code}/`, '/');
  if (hasLanguageCodeOnUrl && !isEnglish) return asPath.replace(`/${code}/`, `/${languageCode}/`);
  if (!hasLanguageCodeOnUrl && !isEnglish) return `/${languageCode}${asPath}`;
  return asPath;
};

const Languages: FC = () => {
  const { asPath } = useRouter();
  const [show, setShow] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const { pmtCurrentLanguageCode } = useGlobalState();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const languageItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const currentLanguage = LANGUAGES.find(({ code }) => code === pmtCurrentLanguageCode) || LANGUAGES[0];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShow(false);
        setFocusedIndex(-1);
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!show) {
          // Open dropdown
          setShow(true);
        } else {
          // Select focused language
          if (focusedIndex >= 0 && focusedIndex < LANGUAGES.length) {
            const selectedLanguage = LANGUAGES[focusedIndex];
            handleLanguageSelect(selectedLanguage.code);
          }
        }
        break;
      case 'Escape':
        setShow(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!show) {
          setShow(true);
        } else {
          const newIndex = (focusedIndex + 1) % LANGUAGES.length;
          setFocusedIndex(newIndex);
          // Scroll to focused item
          setTimeout(() => {
            if (languageItemsRef.current[newIndex]) {
              languageItemsRef.current[newIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }, 0);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (show) {
          const newIndex = (focusedIndex - 1 + LANGUAGES.length) % LANGUAGES.length;
          setFocusedIndex(newIndex);
          // Scroll to focused item
          setTimeout(() => {
            if (languageItemsRef.current[newIndex]) {
              languageItemsRef.current[newIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }, 0);
        }
        break;
    }
  };

  // Handle focus out
  const handleBlur = () => {
    // Use setTimeout to allow click events to process first
    setTimeout(() => {
      // Check if focus moved to an element outside the dropdown
      if (dropdownRef.current) {
        setShow(false);
        setFocusedIndex(-1);
      }
    }, 100);
  };

  const handleLanguageSelect = (languageCode: string) => {
    setShow(false);
    setFocusedIndex(-1);
    // Navigate to the new language page
    window.location.href = getPmtLangHref(languageCode, asPath);
  };

  return (
    <LanguageWrapper ref={dropdownRef}>
      <CurrentLanguageWrapper
        ref={triggerRef}
        aria-label={`Click to ${show ? 'Close' : 'Open'} language selector. Current language: ${currentLanguage?.name}`}
        onClick={() => setShow(!show)}
        onKeyDown={handleKeyDown}
        role='button'
        tabIndex={0}
        onBlur={handleBlur}
        aria-live='polite'
        aria-activedescendant={show && focusedIndex >= 0 ? `language-option-${focusedIndex}` : undefined}
      >
        <LanguageIcon aria-hidden='true' />
        <CurrentLanguageItem>{currentLanguage?.name}</CurrentLanguageItem>
        <ArrowIcon show={show} aria-hidden='true' />
      </CurrentLanguageWrapper>
      <LanguageList show={show} role='listbox' tabIndex={-1} aria-hidden={!show}>
        {LANGUAGES?.map((language: ILanguage, index: number) => (
          <LanguageItem
            key={language.code}
            ref={(el: HTMLDivElement | null) => {
              languageItemsRef.current[index] = el;
            }}
            role='option'
            aria-selected={language.code === currentLanguage?.code}
            aria-label={language.name}
            onClick={() => handleLanguageSelect(language.code)}
            focused={focusedIndex === index}
            id={`language-option-${index}`}
          >
            {language.name}
          </LanguageItem>
        ))}
      </LanguageList>
    </LanguageWrapper>
  );
};

export default Languages;
